import React, { useRef, useEffect, useState, useCallback, FC } from 'react';
import { FieldProps } from "formik";

import { AddressParts, DEV_ADDRESSES, DevAddressRegion, toAddressParts } from './devAddresses';

// Dark, to match Getting Started — the only place that mounts this component.
const DEV_SELECT_STYLE: React.CSSProperties = {
    width: '100%',
    height: '50px',
    padding: '0 12px',
    borderRadius: '8px',
    border: '1.5px solid rgba(255, 255, 255, 0.14)',
    background: '#2b2a3a',
    color: '#ffffff',
    fontSize: '16px',
};

const extractAddressParts = (place: any): AddressParts => {
    const line1 = place.formattedAddress || '';
    let postalCode = '';
    let city = '';
    let state = '';
    let country = '';

    place.addressComponents.forEach((component: any) => {
        const types = component.types;

        if (types.includes("postal_code")) postalCode = component.longText;
        if (types.includes('locality')) city = component.longText;
        if (types.includes('administrative_area_level_1')) state = component.shortText;
        if (types.includes('country')) country = component.shortText;

    });

    return { line1: line1.trim(), postalCode, city, state, country };
};


const PlacesAutocompleteInput: React.FC<FieldProps & React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
    const autocompleteRef = useRef<any>(null);
    const { field } = props;
    const { name } = field;
    const { setFieldValue, setFieldTouched, errors } = props.form;
    const { placeholder } = props;

    let input: HTMLInputElement | null = null;
    let widgetContainer: HTMLElement | null = null;

    const handleBlur = useCallback((event: Event) => {

        input = event.target as HTMLInputElement;
        widgetContainer = input.closest(".widget-container") as HTMLElement;

        const targetValue = input.value.trim();

        setFieldTouched(name, true, false);

        if(!targetValue) {
            widgetContainer.classList.add("error");
        }
        else {
            widgetContainer.classList.remove("error");
        }
    }, [])

    const handleClearing = useCallback( (event: Event) => {
        input = event.target as HTMLInputElement;
        const inputContainer = input.closest(".widget-container") as HTMLElement;
        inputContainer.classList.add("error");
        setFieldValue(name, '');
    }, []);

    const handlePlaceSelected = useCallback(async (event: any) => {
        const place = await event.placePrediction.toPlace();
        await place.fetchFields({
            fields: ["formattedAddress", "addressComponents"]
        });

        widgetContainer?.classList.remove("error");

        if (place?.addressComponents) {
            const parts = extractAddressParts(place);
            setFieldValue(name, JSON.stringify(parts));
        }
    },[field.value]);

    useEffect(() => {
        const el = autocompleteRef.current;
        if (!el) return;

        const shadowRoot = el?.shadowRoot
        let clearButton: HTMLButtonElement | null = null;

        if(shadowRoot) {
            setTimeout(() => {
                widgetContainer = shadowRoot.querySelector(".widget-container") as HTMLElement;
                input = shadowRoot.querySelector(".widget-container input") as HTMLInputElement;
                if (input) {
                    input.setAttribute("placeholder", placeholder || "")
                    input.addEventListener("blur", handleBlur);
                }
                clearButton = shadowRoot.querySelector(".widget-container .clear-button") as HTMLButtonElement;
                if (clearButton) {
                    clearButton.addEventListener("click", handleClearing);
                }
            }, 1000)
        }

        el.addEventListener('gmp-select', handlePlaceSelected);

        return () => {
            el.removeEventListener('gmp-select', handlePlaceSelected);
            if (input) {
                input.removeEventListener("blur", handleBlur);
            }

            if (clearButton) {
                clearButton.removeEventListener("click", handleClearing);
            }
        };
        }, [errors.line1]
    );

    const handleDevAddressSelected = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldTouched(name, true, false);

        const selectedIndex = event.target.value;
        if (selectedIndex === '') {
            setFieldValue(name, '');
            return;
        }

        const parts: AddressParts = toAddressParts(DEV_ADDRESSES[Number(selectedIndex)]);
        setFieldValue(name, JSON.stringify(parts));
    }, [name, setFieldValue, setFieldTouched]);

    // In local dev (where `isDevelopment()` can't detect a VPN/mesh IP), opt out of the
    // paid Google Places API with REACT_APP_MOCK_GOOGLE_PLACES=true and pick a sample.
    const isPlacesMocked = process.env.REACT_APP_MOCK_GOOGLE_PLACES === 'true';

    if (isPlacesMocked) {
        const regions: DevAddressRegion[] = ['Florida', 'Massachusetts', 'Texas'];

        return (
            <select
                aria-label="Sample address (dev)"
                defaultValue=""
                style={DEV_SELECT_STYLE}
                onChange={handleDevAddressSelected}
                onBlur={() => setFieldTouched(name, true, false)}
            >
                <option value="">{placeholder || 'Select a sample address…'}</option>
                {regions.map((region) => (
                    <optgroup key={region} label={region}>
                        {DEV_ADDRESSES.map((address, index) =>
                            address.region === region ? (
                                <option key={address.label} value={index}>
                                    {`${address.label} — ${address.street}, ${address.city}, ${address.state} ${address.postalCode}`}
                                </option>
                            ) : null,
                        )}
                    </optgroup>
                ))}
            </select>
        );
    }

    return (
        <gmp-place-autocomplete
            ref={autocompleteRef}
            included-region-codes="br us pt"
        ></gmp-place-autocomplete>
    )
}



export default PlacesAutocompleteInput;